import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiWithoutCamelization } from "../api";
import type { GeneratorSingleEndpointResponse } from "../components/ObjectModals/GeneratorModal/GeneratorModal.types";
import urls from "../urls";
import { ObjectName } from "../utils/enums";
import { generatorsQueries } from "./useGenerators";
import { generatorSingleEndpointQueries } from "./useGeneratorSingleEndpoint";
import { variantGeneratorsQueries } from "./useVariantGenerators";

/**
 * Hook for generator single endpoint mutations
 * @param siteId - Optional site ID only used to invalidate generators query cache
 */
export function useGeneratorSingleEndpointMutations(siteId?: number) {
  const queryClient = useQueryClient();

  const configuratorQueryKey = [
    ObjectName.Generator,
    { siteOrVariantId: siteId }
  ];

  const editGeneratorSingleEndpoint = useMutation({
    mutationFn: editGeneratorSingleEndpointRequest,
    onSuccess: (response, variables) => {
      const updatedGenerator = response.data;
      queryClient.invalidateQueries({
        queryKey: generatorSingleEndpointQueries.all().queryKey
      });
      queryClient.invalidateQueries({
        queryKey: variantGeneratorsQueries.all().queryKey
      });
      queryClient.invalidateQueries({
        queryKey: configuratorQueryKey
      });
      queryClient.invalidateQueries({
        queryKey: generatorSingleEndpointQueries.detail(variables.id).queryKey
      });
      queryClient.setQueryData(
        generatorSingleEndpointQueries.detail(variables.id).queryKey,
        updatedGenerator
      );
      if (siteId) {
        queryClient.invalidateQueries({
          queryKey: generatorsQueries.list({ siteId }).queryKey
        });
      }
      if (siteId) {
        queryClient.invalidateQueries({
          queryKey: ["siteWizard", { siteId }]
        });
      }
    }
  });

  return {
    editGeneratorSingleEndpoint
  };
}

export function editGeneratorSingleEndpointRequest(
  generator: Partial<GeneratorSingleEndpointResponse>
) {
  if (!generator.id) {
    throw new Error("Cannot edit generator without an id");
  }
  const url = urls.apiWithoutCamelization.generatorSingleEndpoint(generator.id);

  const generatorData: GeneratorSingleEndpointResponse = { ...generator };

  if (generator.anlage_stromstg) {
    generatorData.anlage_stromstg = {
      ...generator.anlage_stromstg,
      generators: undefined // keep list intact until modal handles it
    };
  }
  return apiWithoutCamelization.patch<GeneratorSingleEndpointResponse>(
    url,
    generatorData
  );
}
